import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  UserGroupIcon,
  ClockIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { supabase } from '../../../lib/supabase';
import { useTournament } from '../../TournamentContext';
import { useAppPrefix } from '../../AppPrefixContext';
import { toast } from 'react-hot-toast';

interface Round {
  id: string;
  round_number: number;
  name: string;
  date: string | null;
}

interface GroupPlayer {
  player_id: string;
  sort_order: number;
  player: {
    first_name: string;
    last_name: string;
    category: string;
    handicap: number;
  } | null;
}

interface Group {
  group_number: number;
  tee_time: string | null;
  starting_hole: number | null;
  players: GroupPlayer[];
  completedHoles: number;
}

export default function ScorerGroupsPage() {
  const p = useAppPrefix();
  const navigate = useNavigate();
  const { selectedTournament } = useTournament();
  const tournamentId = selectedTournament?.id || '';

  const [rounds, setRounds] = useState<Round[]>([]);
  const [selectedRoundId, setSelectedRoundId] = useState('');
  const [selectedRound, setSelectedRound] = useState<Round | null>(null);
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(true);
  const [showRoundPicker, setShowRoundPicker] = useState(false);
  const [assignedHoles, setAssignedHoles] = useState<Set<number>>(new Set());

  useEffect(() => {
    if (tournamentId) fetchRounds();
  }, [tournamentId]);

  useEffect(() => {
    if (selectedRoundId) {
      const r = rounds.find(r => r.id === selectedRoundId) || null;
      setSelectedRound(r);
      fetchGroups(selectedRoundId, r?.round_number ?? 1);
      fetchAssignedHoles(selectedRoundId);
    }
  }, [selectedRoundId]);

  const fetchRounds = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Get rounds where this scorer has assignments
      const { data: assignments } = await supabase
        .from('scorer_assignments')
        .select('round_id')
        .eq('scorer_user_id', user.id);

      const assignedRoundIds = [...new Set((assignments ?? []).map((a: { round_id: string }) => a.round_id))];

      if (assignedRoundIds.length === 0) {
        setRounds([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('tournament_rounds')
        .select('id, round_number, name, date')
        .eq('tournament_id', tournamentId)
        .in('id', assignedRoundIds)
        .order('sort_order')
        .order('round_number');
      if (error) throw error;
      const list = data ?? [];
      setRounds(list);
      if (list.length > 0) setSelectedRoundId(list[0].id);
    } catch {
      toast.error('Failed to load rounds');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedHoles = async (roundId: string) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;
    const { data } = await supabase
      .from('scorer_assignments')
      .select('hole_number')
      .eq('round_id', roundId)
      .eq('scorer_user_id', user.id);
    setAssignedHoles(new Set((data ?? []).map((a: { hole_number: number }) => a.hole_number)));
  };

  const fetchGroups = async (roundId: string, roundNumber: number) => {
    setLoading(true);
    try {
      const { data: pairings, error } = await supabase
        .from('player_pairings')
        .select('*')
        .eq('round_id', roundId)
        .order('group_number')
        .order('sort_order');

      if (error) throw error;

      const playerIds = [...new Set((pairings ?? []).map((p: { player_id: string }) => p.player_id))];
      const playerMap = new Map<string, GroupPlayer['player']>();
      if (playerIds.length > 0) {
        const { data: players } = await supabase
          .from('players')
          .select('id, first_name, last_name, category, handicap')
          .in('id', playerIds);
        (players ?? []).forEach((p: { id: string; first_name: string; last_name: string; category: string; handicap: number }) => playerMap.set(p.id, p));
      }

      const { data: holeScores } = await supabase
        .from('hole_scores')
        .select('player_id, hole')
        .eq('tournament_id', tournamentId)
        .eq('round', roundNumber);

      const groupMap = new Map<number, Group>();
      for (const pairing of pairings ?? []) {
        if (!groupMap.has(pairing.group_number)) {
          groupMap.set(pairing.group_number, {
            group_number: pairing.group_number,
            tee_time: pairing.tee_time,
            starting_hole: pairing.starting_hole ?? 1,
            players: [],
            completedHoles: 0,
          });
        }
        const g = groupMap.get(pairing.group_number)!;
        g.players.push({
          player_id: pairing.player_id,
          sort_order: pairing.sort_order,
          player: playerMap.get(pairing.player_id) ?? null,
        });
      }

      // Count holes where at least one player in the group has a score
      for (const group of groupMap.values()) {
        const pids = new Set(group.players.map(p => p.player_id));
        const holesWithScores = new Set<number>();
        for (const hs of holeScores ?? []) {
          if (!pids.has(hs.player_id)) continue;
          holesWithScores.add(hs.hole);
        }
        group.completedHoles = holesWithScores.size;
      }

      setGroups(Array.from(groupMap.values()).sort((a, b) => a.group_number - b.group_number));
    } catch {
      toast.error('Failed to load groups');
    } finally {
      setLoading(false);
    }
  };

  const completedGroups = groups.filter(g => g.completedHoles === 18 && g.players.length > 0).length;

  if (loading && rounds.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  if (rounds.length === 0) {
    return (
      <div className="px-4 py-16 text-center">
        <UserGroupIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
        <p className="text-gray-500 font-medium">No rounds defined</p>
        <p className="text-sm text-gray-400 mt-1">Rounds must be set up by an administrator first.</p>
      </div>
    );
  }

  const GroupCard = ({ group }: { group: Group }) => {
    const isComplete = group.completedHoles === 18 && group.players.length > 0;
    const isPartial = group.completedHoles > 0 && !isComplete;
    return (
      <button
        onClick={() => navigate(`${p}/scorer/group/${group.group_number}?roundId=${selectedRoundId}&round=${selectedRound?.round_number ?? 1}`)}
        className="w-full bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center gap-4 text-left transition-all active:scale-[0.98]"
      >
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isComplete ? 'bg-emerald-100' : isPartial ? 'bg-amber-100' : 'bg-golf-orange/10'
        }`}>
          {isComplete
            ? <CheckCircleIcon className="w-6 h-6 text-emerald-600" />
            : <span className="text-base font-bold text-golf-orange">{group.group_number}</span>
          }
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-golf-gray text-sm">Group {group.group_number}</p>
          <p className="text-xs text-gray-400 mt-0.5 truncate">
            {group.players.map(p => p.player ? `${p.player.first_name} ${p.player.last_name}` : '—').join(', ')}
          </p>
          <div className="flex items-center gap-2 mt-1">
            {group.tee_time && (
              <div className="flex items-center gap-1">
                <ClockIcon className="w-3 h-3 text-gray-400" />
                <span className="text-xs text-gray-400">{group.tee_time.substring(0, 5)}</span>
              </div>
            )}
            {group.starting_hole && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                Hole {group.starting_hole}
              </span>
            )}
          </div>
        </div>
        <div className="flex-shrink-0 flex flex-col items-end gap-1">
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            isComplete ? 'bg-emerald-100 text-emerald-700'
            : isPartial ? 'bg-amber-100 text-amber-700'
            : 'bg-gray-100 text-gray-500'
          }`}>
            {isComplete ? 'Done' : isPartial ? `${group.completedHoles}/18` : 'Pending'}
          </span>
          <ChevronRightIcon className="w-4 h-4 text-gray-300" />
        </div>
      </button>
    );
  };

  return (
    <div className="pb-6">
      <div className="bg-golf-gray px-4 pt-4 pb-5">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-1">Scorer View</p>
        <h1 className="text-xl font-bold text-white">{selectedTournament?.name || 'Tournament'}</h1>
        <div className="relative mt-3">
          <button
            onClick={() => setShowRoundPicker(v => !v)}
            className="flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2"
          >
            <span className="text-white text-sm font-medium">{selectedRound?.name || 'Select Round'}</span>
            <ChevronDownIcon className="w-4 h-4 text-gray-300" />
          </button>
          {showRoundPicker && (
            <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl z-20 w-56 overflow-hidden border border-gray-100">
              {rounds.map(r => (
                <button
                  key={r.id}
                  onClick={() => { setSelectedRoundId(r.id); setShowRoundPicker(false); }}
                  className={`w-full text-left px-4 py-3 text-sm border-b border-gray-100 last:border-0 ${selectedRoundId === r.id ? 'bg-golf-orange/5 text-golf-orange font-semibold' : 'text-golf-gray hover:bg-gray-50'}`}
                >
                  {r.name}
                  {r.date && <span className="text-xs text-gray-400 ml-1">· {new Date(r.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="px-4 py-3 bg-white border-b border-gray-100">
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            <span className="font-semibold text-golf-gray">{groups.length}</span> groups
          </p>
          <div className="flex items-center gap-1.5">
            <CheckCircleIcon className="w-4 h-4 text-emerald-500" />
            <span className="text-sm text-gray-500">
              <span className="font-semibold text-emerald-600">{completedGroups}</span>/{groups.length} complete
            </span>
          </div>
        </div>
        {assignedHoles.size > 0 ? (
          <p className="text-xs text-golf-orange font-medium mt-1">
            Your holes: {[...assignedHoles].sort((a, b) => a - b).join(', ')}
          </p>
        ) : !loading && (
          <p className="text-xs text-red-500 font-medium mt-1">
            No holes assigned to you for this round
          </p>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-16">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-golf-orange border-t-transparent" />
        </div>
      ) : groups.length === 0 ? (
        <div className="px-4 py-16 text-center">
          <UserGroupIcon className="w-12 h-12 text-gray-200 mx-auto mb-3" />
          <p className="text-gray-500 font-medium">No groups yet</p>
          <p className="text-sm text-gray-400 mt-1">Pairings haven't been set up for this round.</p>
        </div>
      ) : (
        <div className="px-4 mt-3 space-y-2">
          {groups.map(group => <GroupCard key={group.group_number} group={group} />)}
        </div>
      )}
    </div>
  );
}
